import type { Filter, FilterDataOption, FilterDataMultiSelect } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import type { TransportData } from '@/domainLogic/index.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { getShowOvernightFilter } from '@/app/common/filters/groups/getShowOvernightFilter.ts';

const OVERNIGHT_FLIGHTS_DEFAULT_VALUE = '0';

const calculateDaysBetweenDates = ({ from, to }: { from: Date; to: Date }) => {
  const fromDate = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const toDate = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());

  return Math.floor((toDate - fromDate) / (1000 * 60 * 60 * 24));
};

const calculateOriginalDate = ({ transportData }: { transportData: TransportData[] }) => {
  const outboundDeparture = transportData?.[0]?.outbound?.segments?.[0]?.departure?.datetime;
  const inboundDeparture = transportData?.[0]?.inbound?.segments?.[0]?.departure?.datetime;
  return inboundDeparture && outboundDeparture
    ? calculateDaysBetweenDates({
        from: new Date(outboundDeparture),
        to: new Date(inboundDeparture),
      })
    : 0;
};

type CreateOvernightFlightsFilterOptions = {
  selectedValue: string;
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  productsData: ProductsData;
  translationDictionary: TranslationDictionary;
};
const createOvernightFlightsFilterOptions = ({
  selectedValue,
  searchParams,
  searchInfoData,
  productsData,
  translationDictionary,
}: CreateOvernightFlightsFilterOptions): FilterDataOption[] => {
  const accData = productsData?.accData;
  const transportData = productsData?.transport?.data;

  const isTrain = searchInfoData.searchMode === SearchModes.TH;
  const filterSelect = isTrain
    ? getTranslationServer('vacation.oss.overnight_train.filter.select', translationDictionary)
    : getTranslationServer('vacation.oss.overnight_flight.filter.select', translationDictionary);
  const filterDeselect = isTrain
    ? getTranslationServer('vacation.oss.overnight_train.filter.deselect', translationDictionary)
    : getTranslationServer('vacation.oss.overnight_flight.filter.deselect', translationDictionary);
  const filterSelectPlural = isTrain
    ? getTranslationServer('vacation.oss.overnight_train.filter.select_plural', translationDictionary)
    : getTranslationServer('vacation.oss.overnight_flight.filter.select_plural', translationDictionary);
  const filterDeselectPlural = isTrain
    ? getTranslationServer('vacation.oss.overnight_train.filter.deselect_plural', translationDictionary)
    : getTranslationServer('vacation.oss.overnight_flight.filter.deselect_plural', translationDictionary);

  const accNights = accData?.accNights || 0;
  const originalNumberOfNights = transportData ? calculateOriginalDate({ transportData }) : 0;
  const overnightNumberOfNights = searchParams[URLParameters.OVERNIGHT_FLIGHTS]
    ? accNights
    : originalNumberOfNights - 1;
  const deselectedKey = originalNumberOfNights > 1 ? filterDeselectPlural : filterDeselect;
  const selectedKey = overnightNumberOfNights > 1 ? filterSelectPlural : filterSelect;
  const deselectedLabel = deselectedKey.replace('__amount__', `${originalNumberOfNights}`);
  const selectedLabel = selectedKey.replace('__amount__', `${overnightNumberOfNights}`);

  return [
    {
      label: deselectedLabel,
      selected: selectedValue === '0',
      value: OVERNIGHT_FLIGHTS_DEFAULT_VALUE,
      isDefault: true,
    },
    {
      label: selectedLabel,
      selected: selectedValue === '1',
      value: '1',
    },
  ];
};

type CreateOvernightFlightsFilters = {
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  localization: Localization;
  productsData: ProductsData;
  translationDictionary: TranslationDictionary;
};
export const createOvernightFlightsFilters = ({
  searchParams,
  searchInfoData,
  localization,
  productsData,
  translationDictionary,
}: CreateOvernightFlightsFilters): Filter[] => {
  const showOvernightFlights = getShowOvernightFilter({
    localization,
    transport: productsData?.transport,
    searchParams,
  });

  const selectedValue = searchParams[URLParameters.OVERNIGHT_FLIGHTS]
    ? searchParams[URLParameters.OVERNIGHT_FLIGHTS]
    : '0';

  const options = createOvernightFlightsFilterOptions({
    selectedValue,
    searchParams,
    searchInfoData,
    productsData,
    translationDictionary,
  });

  const overnightFlightFilter: Filter | undefined = showOvernightFlights
    ? {
        name: FILTER_NAME.OVERNIGHT_FLIGHTS,
        type: FILTER_TYPE.RADIO,
        data: {
          options,
          defaultValue: OVERNIGHT_FLIGHTS_DEFAULT_VALUE,
        } as FilterDataMultiSelect,
      }
    : undefined;

  return [...[overnightFlightFilter]].filter((f) => !!f);
};
